import {
 AboutHotel,
 AboutHotelApiModel,
 DistanceInfo,
 DistanceInformation,
 DistanceList,
 DistanceTerminal,
 Facility,
 HotelCafe,
 HotelHeader,
 HotelSummaryApiModel,
 HotelSummaryModel,
 Policies,
 PoliciesApiModel,
 PolicyContent,
 PolicyHotelCheckingApiModel,
 ReasonToChoose,
 SearchSectionModel,
 Terminal,
 TravelHealth,
} from './hotel';
import {
 todayGregorianIso,
 todayJalali,
 tomorrowJalali,
 toGregorianApiDate,
 toJalaliDate,
} from '@core/services/moment.service';
import { getTerminalTypeName, parseTerminalType, TerminalType } from './terminal-type.enum';

const ABOUT_SECTION_TITLE_CLASS = "sub-title border__right fw-bold position-relative";

const ABOUT_LOCATION_SECTIONS: ReadonlyArray<{
 key: keyof Pick<AboutHotelApiModel, 'AboutNeighborhood' | 'AddressBenefit' | 'AboutStaff'>;
 title: string;
}> = [
 { key: 'AboutNeighborhood', title: 'درباره موقعیت واحد اقامتی' },
 { key: 'AddressBenefit', title: 'مزیت موقعیت مکانی' },
 { key: 'AboutStaff', title: 'درباره کارکنان' },
];

export type NormalizeHotelSummaryOptions = {
 /** Gregorian `YYYY-MM-DD` used when clamping past stay dates. Defaults to local today. */
 referenceGregorianDate?: string;
};

export function normalizeHotelSummary(
 response: HotelSummaryApiModel,
 options?: NormalizeHotelSummaryOptions
): HotelSummaryModel {
 const aboutHotel = normalizeAboutHotel(response.AboutHotel ?? response.About);
 const accommodation = response.Accommodation;
 const amenities = response.Amenities;
 const name = response.Name ?? aboutHotel.FullName ?? '';
 const facilities = response.Facilities ?? amenities?.Facilities ?? [];
 const hotelHeader = normalizeHotelHeader(response, aboutHotel, name);
 const policyHotelChecking =
  response.PolicyHotelChecking ?? getPolicyHotelChecking(response.Policies);
 const hotelCafe = normalizeHotelCafe(response.HotelCafe ?? aboutHotel.HotelCafe);
 const reasonToChoose = normalizeReasonToChoose(
  response.ReasonToChoose ?? aboutHotel.ReasonToChoose
 );

 return {
  ...response,
  HotelId: response.HotelId ?? 0,
  IsActive: response.IsActive ?? true,
  SimilarHotelsLink: response.SimilarHotelsLink ?? null,
  Name: name,
  IsFavourite: response.IsFavourite ?? false,
  IsLogin: response.IsLogin ?? false,
  LowestPriceText: response.LowestPriceText ?? '',
  JsonInfo: response.JsonInfo ?? '',
  JsonFaq: response.JsonFaq ?? '',
  NearBies: response.NearBies ?? response.DistanceInformation?.NearBies ?? [],
  NearStreets: response.NearStreets ?? response.DistanceInformation?.NearStreets ?? [],
  PageGeneratorLinks: response.PageGeneratorLinks ?? [],
  Type: response.Type ?? accommodation?.Type ?? 1,
  BreadCrumb: (response.BreadCrumb ?? []).map((item, index, items) => ({
   ...item,
   AriaCurrent: item.AriaCurrent ?? index === items.length - 1,
  })),
  CityHotels: response.CityHotels ?? {
   Url: response.BreadCrumb?.[1]?.Url ?? '',
   Count: 0,
   Name: '',
  },
  AboutHotel: aboutHotel,
  HouseId: response.HouseId ?? accommodation?.HouseId ?? '',
  PersonCapacity:
   response.PersonCapacity ??
   accommodation?.PersonCapacity ??
   ({} as HotelSummaryModel['PersonCapacity']),
  Labels: response.Labels ?? { Tags: [], Promotions: [], IHOPlus: false },
  HotelCafe: hotelCafe,
  HotelInternetParking:
   response.HotelInternetParking ?? amenities?.HotelInternetParking ?? createEmptyInternetParking(),
  ModalHotelGallery: {
   HotelName: response.ModalHotelGallery?.HotelName ?? name,
   IsFavourite: response.ModalHotelGallery?.IsFavourite ?? response.IsFavourite ?? false,
   IsLogin: response.ModalHotelGallery?.IsLogin ?? response.IsLogin ?? false,
   Star: response.ModalHotelGallery?.Star ?? hotelHeader.Star,
   DiscountBalloon: response.ModalHotelGallery?.DiscountBalloon ?? null,
   Price: response.ModalHotelGallery?.Price ?? hotelHeader.Price,
   Pictures: (response.ModalHotelGallery?.Pictures ?? []).map((picture) => ({
    ...picture,
    Lazy: picture.Lazy ?? false,
    Categories: picture.Categories ?? [],
   })),
  },
  HotelHeader: hotelHeader,
  LabelHotelReview: response.LabelHotelReview ?? {},
  ActivePromotions: response.ActivePromotions ?? [],
  MetaTags: response.MetaTags ?? { Title: '', PageDescription: '', Keywords: '' },
  DistanceInfo: response.DistanceInfo ?? normalizeDistanceInformation(response.DistanceInformation),
  FAQs: response.FAQs ?? [],
  Votes: (response.Votes ?? []).map((vote) => ({
   ...vote,
   AddDatePersian: toJalaliDate(vote.AddDate || vote.AddDatePersian) || vote.AddDatePersian || '',
   ArrivalDatePersian:
    toJalaliDate(vote.ArrivalDate || vote.ArrivalDatePersian) || vote.ArrivalDatePersian || '',
   AnswerDatePersian:
    toJalaliDate(vote.AnswerDate || vote.AnswerDatePersian) || vote.AnswerDatePersian || '',
  })),
  Facilities: facilities,
  Policies: normalizePolicies(response.Policies, response.HotelId),
  PolicyHotelChecking: policyHotelChecking,
  SearchSection: normalizeSearchSection(
   response.SearchSection,
   name,
   options?.referenceGregorianDate
  ),
  ReasonToChoose: reasonToChoose,
  TravelHealth: response.TravelHealth ?? normalizeTravelHealth(facilities),
  limitCountNightRange: response.limitCountNightRange ?? null,
  ClubOffers: response.ClubOffers ?? [],
  HostInfo: response.HostInfo ?? accommodation?.HostInfo ?? undefined,
  AccomodationSpecifications:
   response.AccomodationSpecifications ?? accommodation?.AccomodationSpecifications ?? [],
  ParentId: response.ParentId ?? accommodation?.ParentId ?? '',
  Rules: response.Rules ?? accommodation?.Rules ?? null,
  ShowInstallmentBanner: response.ShowInstallmentBanner ?? false,
 } as HotelSummaryModel;
}

/**
 * getHotelSummaryInfo reports the stay in Gregorian dates, while the datepicker and every
 * PDP consumer of StartDate/EndDate expect Jalali. Convert once here and keep the Gregorian
 * values on the *Georgian fields for callers that need them.
 */
function normalizeSearchSection(
 source: SearchSectionModel | undefined,
 name: string,
 referenceGregorianDate: string = todayGregorianIso()
): SearchSectionModel {
 let startDate = toJalaliDate(source?.StartDate || source?.StartDateGeorgian);
 let endDate = toJalaliDate(source?.EndDate || source?.EndDateGeorgian);
 let startDateGeorgian = toGregorianApiDate(source?.StartDateGeorgian || source?.StartDate);
 let endDateGeorgian = toGregorianApiDate(source?.EndDateGeorgian || source?.EndDate);
 let night = source?.Night ?? 0;

 if (startDateGeorgian && startDateGeorgian < referenceGregorianDate) {
  startDate = todayJalali(referenceGregorianDate);
  endDate = tomorrowJalali(referenceGregorianDate);
  startDateGeorgian = referenceGregorianDate;
  endDateGeorgian = toGregorianApiDate(endDate);
  night = 1;
 }

 return {
  StartDate: startDate,
  EndDate: endDate,
  StartDateGeorgian: startDateGeorgian,
  EndDateGeorgian: endDateGeorgian,
  Night: night,
  SearchBox: source?.SearchBox ?? name,
 };
}

function normalizeHotelHeader(
 response: HotelSummaryApiModel,
 aboutHotel: AboutHotel,
 name: string
): HotelHeader {
 const current = response.HotelHeader;
 const sourceStar = current?.Star ?? aboutHotel.Star;
 const gradeTitle = sourceStar?.GradeTitle;
 const gradeIdFromTitle = gradeTitle ? Number(gradeTitle) : 0;

 return {
  ...current,
  FullName: current?.FullName ?? aboutHotel.FullName ?? name,
  TypeName: current?.TypeName ?? aboutHotel.TypeName ?? '',
  IsFavourite: current?.IsFavourite ?? response.IsFavourite ?? false,
  IsLogin: current?.IsLogin ?? response.IsLogin ?? false,
  RoomName: current?.RoomName ?? null,
  Score: current?.Score ??
   aboutHotel.Score ?? {
    ScoreNumber: 0,
    Text: '',
    Description: null,
    SolidType: null,
    Total: 0,
   },
  Star: {
   ...sourceStar,
   GradeId: sourceStar?.GradeId ?? (Number.isNaN(gradeIdFromTitle) ? 0 : gradeIdFromTitle),
   StarClassName: sourceStar?.StarClassName ?? (gradeTitle ? 's-' + gradeTitle : undefined),
  },
  Address: current?.Address ??
   aboutHotel.Address ?? {
    Address: '',
    CityName: '',
    Long: null,
    Lat: null,
    State: '',
   },
  Labels: current?.Labels ?? { BaseClassName: '', Labels: [] },
  Price: current?.Price ??
   aboutHotel.Price ?? {
    StartingText: '',
    OldPrice: false,
    BoardPrice: 0,
    IhoPrice: 0,
    Nights: 0,
   },
  Discount: current?.Discount ?? null,
  IhoMapUrl: current?.IhoMapUrl ?? aboutHotel.IhoMapUrl ?? '',
  IhoMapImage: current?.IhoMapImage ?? aboutHotel.IhoMapImage ?? '',
  ForeignName: current?.ForeignName ?? aboutHotel.ForeignName ?? null,
 };
}

function normalizeDistanceInformation(source?: DistanceInformation): DistanceInfo {
 const terminals = (source?.TerminalList ?? []).map(mapTerminal);
 const holyShrines = terminals.filter((terminal) => terminal.Type === TerminalType.HolyShrine);
 const accessTerminals = terminals.filter((terminal) => terminal.Type !== TerminalType.HolyShrine);
 const districtPlaces: DistanceList[] = (source?.DistrictList ?? []).map((item) => ({
  Name: item.Name,
  Duration: item.Duration,
  Distance: normalizeDistanceDisplay(item.Distance),
 }));

 return {
  DistanceList: [...holyShrines.map(holyShrineToPlace), ...districtPlaces],
  Terminals: accessTerminals,
  Airports: accessTerminals.filter((terminal) => terminal.Type === TerminalType.AirPort),
  MetroLines: accessTerminals.filter((terminal) => terminal.Type === TerminalType.MetroLine),
  HolyShrines: holyShrines,
 };
}

/** Type 4 (HolyShrine) is shown at the top of «مکان های مهم دیگر», not under terminals. */
function holyShrineToPlace(terminal: DistanceTerminal): DistanceList {
 const amount = terminal.Distance == null ? '' : String(terminal.Distance);
 const unit = normalizeDistanceDisplay(terminal.DistanceUnitName ?? '');

 return {
  Name: terminal.Name,
  Duration: '',
  Distance: [amount, unit].filter((part) => part !== '').join(' '),
 };
}

function mapTerminal(terminal: Terminal): DistanceTerminal {
 const type = parseTerminalType(terminal.Type);

 return {
  Name: terminal.Name,
  Distance: normalizeDistanceValue(terminal.Distance?.Amount),
  DistanceUnitName: terminal.Distance?.Unit ?? '',
  Type: type,
  TypeName: type != null ? getTerminalTypeName(type) : '',
 };
}

/** API may send Arabic decimal separator (٫) which renders like a slash: 1/7. */
function normalizeDistanceDisplay(value?: string): string {
 return replaceArabicDecimalSeparator(value ?? '');
}

function normalizeDistanceValue(value?: string | number | null): string | number {
 if (typeof value === 'number') return value;
 return replaceArabicDecimalSeparator(value ?? '');
}

function replaceArabicDecimalSeparator(value: string): string {
 return value.replace(/\u066B/g, '.').replace(/(\d)\/(\d)/g, '$1.$2');
}

function normalizeAboutHotel(source?: AboutHotelApiModel): AboutHotel {
 const nested = source?.About;
 const about: AboutHotelApiModel = {
  ...source,
  AboutProperty: pickText(source?.AboutProperty, nested?.AboutProperty),
  AboutNeighborhood: pickText(source?.AboutNeighborhood, nested?.AboutNeighborhood),
  AddressBenefit: pickText(source?.AddressBenefit, nested?.AddressBenefit),
  AboutStaff: pickText(source?.AboutStaff, nested?.AboutStaff),
 };
 const hasStructuredAbout =
  hasText(about.AboutProperty) ||
  hasText(about.AboutNeighborhood) ||
  hasText(about.AddressBenefit) ||
  hasText(about.AboutStaff);

 const briefDescription = hasStructuredAbout
  ? buildAboutParagraph(about.AboutProperty)
  : about.BriefDescription ?? '';
 const description = hasStructuredAbout
  ? buildAboutLocationHtml(about)
  : about.Description ?? '';

 return {
  ...about,
  BriefDescription: briefDescription,
  Description: description,
  HotelCafe: normalizeHotelCafe(about.HotelCafe) ?? undefined,
  ReasonToChoose: normalizeReasonToChoose(about.ReasonToChoose) ?? undefined,
 };
}

/** Only emits title + body when that field has text; empty fields omit their title too. */
function buildAboutLocationHtml(about: AboutHotelApiModel): string {
 const sections: string[] = [];

 for (const { key, title } of ABOUT_LOCATION_SECTIONS) {
  const content = about[key];
  if (!hasText(content)) continue;

  sections.push(
   `<p class='${ABOUT_SECTION_TITLE_CLASS}'>${title}</p>` +
    `<p>${plainTextToHtml(content)}</p>`
  );
 }

 return sections.join('');
}

function buildAboutParagraph(value?: string): string {
 if (!hasText(value)) return '';
 return (
  `<p class='${ABOUT_SECTION_TITLE_CLASS}'>درباره واحد اقامتی</p>` +
  `<p>${plainTextToHtml(value)}</p>`
 );
}

function normalizeHotelCafe(source?: HotelCafe | null): HotelCafe | null {
 if (!hasText(source?.Description)) return null;

 return {
  Description: normalizeLegacyOrPlainHtml(source!.Description),
 };
}

function normalizeReasonToChoose(
 source?: ReasonToChoose | null
): ReasonToChoose | null {
 if (!source) return null;

 const rateComment = hasText(source.RateComment)
  ? normalizeLegacyOrPlainHtml(source.RateComment)
  : '';
 const reasonToChoose = hasText(source.ReasonToChoose)
  ? normalizeLegacyOrPlainHtml(source.ReasonToChoose)
  : '';

 if (!rateComment && !reasonToChoose) return null;

 return {
  RateComment: rateComment,
  ReasonToChoose: reasonToChoose,
 };
}

function normalizePolicies(source: PoliciesApiModel | undefined, hotelId?: number): Policies {
 return {
  PolicyGroup: source?.PolicyGroup ?? [],
  CheckInTime: source?.CheckInTime ?? '',
  CheckOutTime: source?.CheckOutTime ?? '',
  IsHourly: source?.IsHourly ?? false,
  PolicyHotelCancellation: normalizePolicyContent(source?.PolicyHotelCancellation),
  PolicyHotelChild: normalizeTextList(source?.PolicyHotelChild),
  PolicyHotelChecking: normalizeHotelChecking(source?.PolicyHotelChecking),
  HotelId: source?.HotelId ?? hotelId,
  SpecialPolicies: (source?.SpecialPolicies ?? []).map((item) =>
   normalizeLegacyOrPlainHtml(item)
  ),
 };
}

function normalizePolicyContent(value: string | PolicyContent[] | undefined): string {
 if (typeof value === 'string') return normalizeLegacyOrPlainHtml(value);

 return (value ?? [])
  .map((item) =>
   [
    item.Title ? '<strong>' + escapeHtml(item.Title) + '</strong>' : '',
    plainTextToHtml(item.Description ?? ''),
   ]
    .filter(Boolean)
    .join('<br/>')
  )
  .filter(Boolean)
  .join('<br/>');
}

function normalizeTextList(value: string | string[] | undefined): string {
 if (Array.isArray(value)) {
  return value.filter(Boolean).map((item) => plainTextToHtml(item)).join('<br/>');
 }
 return normalizeLegacyOrPlainHtml(value ?? '');
}

function normalizeHotelChecking(
 value: string | PolicyHotelCheckingApiModel | null | undefined
): string {
 return typeof value === 'string'
  ? normalizeLegacyOrPlainHtml(value)
  : (value?.ChekingDetails ?? [])
     .filter(Boolean)
     .map((item) => plainTextToHtml(item))
     .join('<br/>');
}

function getPolicyHotelChecking(source?: PoliciesApiModel): PolicyHotelCheckingApiModel | null {
 const value = source?.PolicyHotelChecking;
 return value && typeof value !== 'string' ? value : null;
}

function normalizeTravelHealth(facilities: Facility[]): TravelHealth | undefined {
 const travelHealth = facilities.find((facility) => facility.Id === 15);
 if (!travelHealth) return undefined;

 return {
  Title: travelHealth.Name,
  Description: travelHealth.Description ?? '',
  HotelFacilities: travelHealth.HotelFacilities ?? [],
 };
}

function replaceNewlines(value: string): string {
 return value.replace(/\r\n|\n|\r/g, '<br />');
}

function plainTextToHtml(value: string): string {
 return replaceNewlines(escapeHtml(value));
}

/** Keeps HTML returned by the legacy endpoint while treating the v2 contract as plain text. */
function normalizeLegacyOrPlainHtml(value: string): string {
 return /<\/?[a-z][\s\S]*>/i.test(value) ? replaceNewlines(value) : plainTextToHtml(value);
}

function escapeHtml(value: string): string {
 return value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
}

function hasText(value?: string | null): value is string {
 return !!value && value.trim().length > 0;
}

function pickText(...values: Array<string | null | undefined>): string | undefined {
 return values.find(hasText);
}

function createEmptyInternetParking() {
 return {
  InternetClassName: '',
  InternetTitle: '',
  InternetDescription: '',
  InternetIcon: null,
  ParkingClassName: '',
  ParkingTitle: '',
  ParkingDescription: '',
 };
}
